import { Typography } from '@ori-ui/mui';
import { useApiClient, updateCustomerRegistrationSponsorDetails, HttpStatusCodes } from '@ori/global-api-client-react';
import type {
  AxiosError,
  GlobalCustomerRegistrationsRegistrationSponsorDetailsDto,
  GlobalSharedAddressDto,
} from '@ori/global-api-client-react';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';

import { Localization } from '../../Localization';
import { defaultTranslations } from '../../constants/defaultTranslations_simplified-registration-BrandPartner';
import {
  useConfiguration,
  useNotificationData,
  useFormError,
  useStepperData,
  useVirtualPageAnalyticsData,
} from '../../contexts';
import type { IErrors } from '../../interfaces';
import { MessageType } from '../../interfaces';
import { FetchResult, TranslationKey } from '../../models';
import {
  useSponsorData,
  useSponsorInfoData,
  useSponsorMetadata,
  usePersonalData,
  useSponsorImage,
} from '../../models/api';
import { scrollTopFunctionHelper, virtualPageEventDispatch } from '../../utils';
import { BasicSkeleton } from '../BasicSkeleton';
import { ConnectToBPForm } from '../Forms';
import { RootContainer, StyledTitle } from './StepsStyledComponents';

export const SponsorStep: FC = () => {
  const [fetchKey, setFetchKey] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const { registrationId } = useConfiguration();

  const notificationStore = useNotificationData().context.notificationData;
  const {
    context: { errorData },
  } = useFormError();
  const { setErrorData, customErrors } = errorData;

  const {
    context: { stepperData },
  } = useStepperData();
  const { activeStep, setActiveStep } = stepperData;
  const {
    virtualPageAnalyticsContext: { virtualPageAnalyticsData },
  } = useVirtualPageAnalyticsData();
  const { setPreviousStep, previousStep } = virtualPageAnalyticsData;

  const { data: sponsorConfig } = useSponsorMetadata();
  const { data: sponsorInitialData } = useSponsorData();
  const { data: sponsorInfoData } = useSponsorInfoData(fetchKey);
  const { data: personalInitialData } = usePersonalData();
  const sponsorImage = useSponsorImage(registrationId, sponsorInfoData?.profileImageDocumentId);

  const api = useApiClient();

  const oneCountryMarket =
    sponsorConfig?.address?.country?.isAvailable && sponsorConfig.address.country.availableOptions.length === 1;

  useEffect(() => {
    scrollTopFunctionHelper();
  }, []);

  useEffect(() => {
    virtualPageEventDispatch('SponsorSelectionStep', previousStep, registrationId);
    setPreviousStep('SponsorSelectionStep');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- virtual page event should be fired exacly once, no need to reevaluate
  }, []);

  const defaultAddressType = sponsorConfig?.address?.addressType?.availableOptions
    ? sponsorConfig.address.addressType.availableOptions[0]
    : 'Other';

  const onSubmit: SubmitHandler<GlobalCustomerRegistrationsRegistrationSponsorDetailsDto> = async (data) => {
    let correctAddressData = null;
    let correctSponsorNumber = data.sponsorNumber as number | null | undefined;

    if (data.address) {
      correctAddressData = {
        address: { ...data.address, addressType: defaultAddressType },
      } as unknown;
    }

    if (data.sponsorNumber) {
      correctAddressData = null;
    }

    if (correctSponsorNumber === null) {
      correctSponsorNumber = undefined;
    } else {
      correctSponsorNumber = Number(data.sponsorNumber);
    }

    if (!data.sponsorNumber && personalInitialData?.address?.country) {
      correctAddressData = {
        address: { ...personalInitialData.address },
      };
    }

    if (!data.sponsorNumber && !data.address && oneCountryMarket) {
      correctAddressData = {
        address: {
          addressType: defaultAddressType,
          country: sponsorConfig.address?.country?.availableOptions[0],
        },
      };
    }

    const correctData: GlobalCustomerRegistrationsRegistrationSponsorDetailsDto = {
      ...(correctAddressData as GlobalSharedAddressDto),
      sponsorNumber: correctSponsorNumber,
      sponsorOrigin: data.sponsorOrigin,
    };
    try {
      const updateResponse = await updateCustomerRegistrationSponsorDetails(api, registrationId, correctData);
      const result = new FetchResult<unknown>(updateResponse.status);

      if (result.isSuccess()) {
        setSubmitSuccess(true);
        if (correctData.sponsorNumber) {
          setActiveStep(activeStep + 1);
        } else {
          setFetchKey(Date.now().toString());
        }
        if (customErrors && customErrors.length > 0) {
          setErrorData([]);
        }
      }
    } catch (error: unknown) {
      const errorResponse = error as AxiosError;
      if (errorResponse.response?.status === HttpStatusCodes.status400BadRequest) {
        const { Errors } = errorResponse.response.data as IErrors;
        const submitErrors = Errors?.map((er) => ({
          fieldName: er.PropertyName,
          errorCode: er.Code,
        }));
        if (submitErrors?.length && submitErrors.length > 0) {
          setErrorData(submitErrors);
        } else {
          setErrorData([]);
        }
      } else {
        notificationStore.setNotificationData({
          isOpen: true,
          message: (
            <Localization
              id={TranslationKey.cannotUpdate}
              defaultMessage={defaultTranslations.cannotUpdate}
            />
          ),
          type: MessageType.error,
        });
      }
    }
  };

  if (!sponsorInitialData) {
    return <BasicSkeleton />;
  }

  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- we need logical operator ||
  const defaultAddressAvailable = personalInitialData?.address?.country || oneCountryMarket;

  return (
    <RootContainer>
      <StyledTitle
        variant="h4"
        color="textPrimary"
      >
        <Localization
          id={TranslationKey.SecondStep_Header}
          defaultMessage={defaultTranslations.SecondStep_Header}
        />
      </StyledTitle>
      <Typography
        variant="subtitle1"
        color="textPrimary"
        align="center"
      >
        <Localization
          id={TranslationKey.SecondStep_Text}
          defaultMessage={defaultTranslations.SecondStep_Text}
        />
      </Typography>
      <ConnectToBPForm
        config={sponsorConfig}
        initialData={sponsorInitialData}
        sponsorInfo={sponsorInfoData}
        profileSponsorImage={sponsorImage}
        isDefaultAddress={Boolean(defaultAddressAvailable)}
        isSuccessSubmit={submitSuccess}
        onSubmitForm={onSubmit}
      />
    </RootContainer>
  );
};
